import { EmbedPageContent } from "@/features/embeds";
import { EmbedPage } from "@/features/supabase";
import { useGetEmbedPagesQuery } from "@/store/services/supabase";
import { Tool } from "@/types/tools";
import { useDisclosure } from "@mantine/hooks";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { Button, Skeleton } from "antd";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { DatasetSelectModal } from "./components/DatasetSelectModal";
import { DatasetSidebar } from "./components/DatasetSidebar";
import { TopBar } from "./components/TopBar";
import { sortDatasets } from "./functions";
const Render = () => {
  const router = useRouter();
  const {
    data: datasets = [],
    isLoading
  } = useGetEmbedPagesQuery();
  const [selectedDataset, setSelectedDataset] = useState<EmbedPage>();
  const [isModalOpen, modalHandlers] = useDisclosure();
  const [isSidebarOpen, sidebarHandlers] = useDisclosure(true);
  const selectDataset = useCallback((dataset: EmbedPage) => {
    setSelectedDataset(dataset);
    router.replace({
      pathname: router.pathname,
      query: {
        ...router.query,
        dataset: dataset.slug
      }
    }, undefined, {
      shallow: true
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [router.pathname]);
  useEffect(() => {
    // router.query is empty until the router hydrates, so acting earlier would
    // auto-select the default over the top of a deep link.
    if (selectedDataset || !router.isReady || !datasets.length) return;
    const slug = typeof router.query.dataset === "string" ? router.query.dataset : undefined;
    const linked = slug ? datasets.find(dataset => dataset.slug === slug) : undefined;
    if (linked?.can_open) {
      setSelectedDataset(linked);
      return;
    }

    // A dataset can be listed without being openable, so a deep link to one the org
    // isn't entitled to falls through to the picker rather than mounting the embed.
    if (linked) {
      modalHandlers.open();
      return;
    }
    const firstOpenable = sortDatasets(datasets).find(dataset => dataset.can_open);
    if (firstOpenable) selectDataset(firstOpenable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasets, router.isReady, router.query.dataset]);

  // Toggling the sidebar resizes the embed's container. Embedded iframes generally
  // only re-measure on a window resize, so nudge them -- same trick as
  // HubLayoutResizable does for maps.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  }, [isSidebarOpen]);
  const handleSelect = (dataset: EmbedPage) => {
    selectDataset(dataset);
    modalHandlers.close();
  };
  return <div className="flex h-full w-full flex-col" data-sentry-component="Render" data-sentry-source-file="index.tsx">
      <TopBar selectedDataset={selectedDataset} isSidebarOpen={isSidebarOpen} onOpenDatasetSelect={modalHandlers.open} onToggleSidebar={sidebarHandlers.toggle} data-sentry-element="TopBar" data-sentry-source-file="index.tsx" />

      <div className="flex min-h-0 flex-1">
        {selectedDataset && isSidebarOpen && <DatasetSidebar dataset={selectedDataset} />}

        {/* The arbitrary-child selector is what gives Embed its height -- it has no
            height of its own, so this chain must stay intact. */}
        <div className="flex min-h-0 flex-1 flex-col [&>*]:min-h-0 [&>*]:flex-1">
          {selectedDataset ? <EmbedPageContent {...selectedDataset} /> : <EmptyState isLoading={isLoading} hasDatasets={datasets.length > 0} onBrowse={modalHandlers.open} />}
        </div>
      </div>

      <DatasetSelectModal open={isModalOpen} selectedDataset={selectedDataset} onClose={modalHandlers.close} onSelect={handleSelect} data-sentry-element="DatasetSelectModal" data-sentry-source-file="index.tsx" />
    </div>;
};
interface EmptyStateProps {
  isLoading: boolean;
  hasDatasets: boolean;
  onBrowse: () => void;
}

/**
 * Shown only when nothing could be auto-selected: either the org has no datasets at
 * all, or every dataset it can see is locked.
 */
const EmptyState = ({
  isLoading,
  hasDatasets,
  onBrowse
}: EmptyStateProps) => {
  if (isLoading) {
    return <div className="p-8">
        <Skeleton active paragraph={{
        rows: 6
      }} />
      </div>;
  }
  return <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-8 text-center" data-sentry-component="EmptyState" data-sentry-source-file="index.tsx">
      <MagnifyingGlassIcon size={40} className="text-gray-300" weight="bold" data-sentry-element="MagnifyingGlassIcon" data-sentry-source-file="index.tsx" />
      <span className="text-base font-medium text-gray-800">
        {hasDatasets ? "No datasets are unlocked yet" : "No datasets are available"}
      </span>
      <span className="max-w-[420px] text-sm text-gray-500">
        Contact your Customer Success Manager to request access to any locked datasets, or to discuss unlisted
        datasets.
      </span>
      {hasDatasets && <Button type="primary" onClick={onBrowse}>
          Browse Datasets
        </Button>}
    </div>;
};
export default {
  id: "explorer",
  category: "reference",
  keyPrefix: "reference.explorer",
  icon: <MagnifyingGlassIcon type="bold" />,
  fullScreen: true,
  render: () => <Render />
} as Tool;